import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Info, Calculator, Stethoscope, Scale, Activity, ShieldCheck, ClipboardList } from 'lucide-react';

const ChallengeSection = () => {
  const { t } = useLanguage();

  const [height, setHeight] = React.useState('');
  const [weight, setWeight] = React.useState('');
  const [bmi, setBmi] = React.useState('');

  useEffect(() => {
    const h = parseFloat(height);
    const w = parseFloat(weight);
    setBmi(w > 0 && h > 0 ? (w / (h * h)).toFixed(1) : '');
  }, [weight, height]);

  /* Old bmiCategories
  const bmiCategories = [
    { label: 'UNDERWEIGHT', world: '<18.5', asian: '-', min: 0, max: 18.4, badgeBg: '#E8F0FF', badgeText: '#4E73DF' },
    { label: 'NORMAL', world: '23', asian: '19', min: 18.5, max: 22.9, badgeBg: '#E8F8EC', badgeText: '#4CAF73' },
    { label: 'OVERWEIGHT', world: '25', asian: '23', min: 23, max: 24.9, badgeBg: '#FFF6D8', badgeText: '#C28A1D' },
    { label: 'OBESITY Grade I', world: '30', asian: '25', min: 25, max: 29.9, badgeBg: '#FFEEDF', badgeText: '#D96C3D' },
    { label: 'OBESITY Grade II', world: '35', asian: '30', min: 30, max: 34.9, badgeBg: '#FFE5E5', badgeText: '#D9534F' },
    { label: 'OBESITY Grade III', world: '-', asian: '35', min: 35, max: 39.9, badgeBg: '#F3E8FF', badgeText: '#9B5DE5' },
    { label: 'SEVERE', world: '40', asian: '40', min: 40, max: 100, badgeBg: '#FFE5E5', badgeText: '#D9534F' },
  ];
  */

  const bmiCategories = [
    {
      label: 'UNDERWEIGHT',
      world: '< 18.5', worldBg: '', worldText: '#444',
      asian: '—', asianBg: '', asianText: '#444',
      min: 0, max: 18.4,
    },
    {
      label: 'NORMAL',
      world: '23', worldBg: '#C8D9A0', worldText: '#4a5a1e',
      asian: '19', asianBg: '#A8CDD8', asianText: '#1e4a5a',
      min: 18.5, max: 22.9,
    },
    {
      label: 'OVERWEIGHT',
      world: '25', worldBg: '#C8D9A0', worldText: '#4a5a1e',
      asian: '23', asianBg: '#A8CDD8', asianText: '#1e4a5a',
      min: 23, max: 24.9,
    },
    {
      label: 'OBESITY Grade I',
      world: '30', worldBg: '#F5E88A', worldText: '#6b5200',
      asian: '25', asianBg: '#F5E88A', asianText: '#6b5200',
      min: 25, max: 29.9,
    },
    {
      label: 'OBESITY Grade II',
      world: '35', worldBg: '#E5844A', worldText: '#fff',
      asian: '30', asianBg: '#F5E88A', asianText: '#6b5200',
      min: 30, max: 34.9,
    },
    {
      label: 'OBESITY Grade III',
      world: '—', worldBg: '', worldText: '#444',
      asian: '35', asianBg: '#E5844A', asianText: '#fff',
      min: 35, max: 39.9,
    },
    {
      label: 'SEVERE',
      world: '40', worldBg: '#C0392B', worldText: '#fff',
      asian: '40', asianBg: '#C0392B', asianText: '#fff',
      min: 40, max: 100,
    },
  ];

  // Logic to find the active category for highlighting
  const activeCategory = bmi ? bmiCategories.find(c => parseFloat(bmi) >= c.min && parseFloat(bmi) <= c.max) : null;

  return (
    <section id="challenge" className="py-20 bg-[#F8FAFC] scroll-mt-28" data-testid="challenge-section">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/*
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          OLD SECTION ORDER (before refactor):
          1. Who should consult us?
          2. HEALTH IMPACTS OF OBESITY (header)
          3. Grid lg:col-span-7 | lg:col-span-5
               LEFT  (7): Clinical Evaluation + BMI Calculator (stacked)
               RIGHT (5): BMI Classification Table
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          NEW SECTION ORDER (after refactor):
          1. Who should consult us?
          2. BMI Calculator  ← MOVED HERE (full-width, max-w-5xl)
          3. HEALTH IMPACTS OF OBESITY (header)
          4. Grid lg:col-span-6 | lg:col-span-6  (equal columns)
               LEFT  (6): Clinical Evaluation
               RIGHT (6): BMI Classification Table
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        */}

        {/* ── 1. Who should consult us? ─────────────────────────────────── */}
        {/* <div className="mb-16 flex justify-center"> */}
        <div className="flex justify-center mb-12 md:mb-16">
          <div className="animate-autoscale inline-block bg-white p-8 md:p-12 rounded-[40px] border border-slate-100 shadow-xl max-w-4xl w-full">
            {/* <h1 className='text-3xl md:text-4xl mb-8 text-[#800000] font-black tracking-tight text-center'>
              Who should consult us?
            </h1> */}
            <h1 className="text-3xl md:text-4xl pb-6 text-[#800000] font-black tracking-tight text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Who should consult us?
            </h1>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 text-left">
              {[
                "Difficulty losing weight despite efforts",
                "Weight regain after dieting",
                "Obesity with diabetes / fatty liver",
                "Considering bariatric surgery",
                "Looking for medically supervised weight loss"
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                  <span className="font-bold text-slate-700 text-sm md:text-base leading-tight">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 2. BMI Calculator & Table Side-by-Side ─────────────────────────── */}
        <div className="max-w-5xl mx-auto mb-20 grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          
          {/* Left: Calculator */}
          <div className="bg-slate-900 rounded-3xl p-1 shadow-2xl shadow-red-950/20 flex flex-col h-full">
            <div className="bg-slate-800/50 rounded-[22px] p-8 flex flex-col justify-between h-full">
              
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <Calculator className="w-5 h-5 text-red-400" />
                    <span className="text-xs font-bold uppercase tracking-wider">Health Tool</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>BMI Calculator</h3>
                </div>

                <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-white/5 shrink-0">
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase font-bold px-2">Your Result</p>
                    <div className="text-3xl font-mono font-black text-white px-2">
                      {bmi || '--.-'}
                    </div>
                  </div>
                  {activeCategory && (
                    <div
                      className="px-4 py-2 rounded-xl text-xs font-bold transition-all duration-500"
                      style={{ backgroundColor: activeCategory.worldBg || '#334155', color: activeCategory.worldText || '#fff' }}
                    >
                      {activeCategory.label}
                    </div>
                  )}
                </div>
              </div>

              {/* Centered Inputs (Stacked for elegant, non-squished form spacing) */}
              <div className="grid grid-cols-1 gap-5 my-auto py-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Weight (kg)</label>
                  <div className="relative">
                    <Scale className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400/50 pointer-events-none" />
                    <input
                      type="number"
                      step="any"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="00"
                      className="w-full pl-12 pr-4 py-4 bg-slate-900 border border-slate-700 rounded-2xl text-white focus:ring-2 focus:ring-[#c0392b] focus:border-transparent outline-none transition-all placeholder:text-slate-700"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Height (meters)</label>
                  <div className="relative">
                    <Activity className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400/50 pointer-events-none" />
                    <input
                      type="number"
                      step="any"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="1.75"
                      className="w-full pl-12 pr-4 py-4 bg-slate-900 border border-slate-700 rounded-2xl text-white focus:ring-2 focus:ring-[#c0392b] focus:border-transparent outline-none transition-all placeholder:text-slate-700"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-white/5 space-y-2">
                <div className="flex items-center gap-2 text-slate-500 text-xs italic">
                  <Info className="w-3.5 h-3.5" />
                  <span> Body Mass Index is a universal screening tool using height and weight to estimate body fat levels.</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 text-xs italic">
                  <Info className="w-3.5 h-3.5" />
                  <span>Formula: Weight / (Height × Height)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: BMI Classification Table */}
          <div className="rounded-2xl overflow-hidden border-2 border-[#c0392b] shadow-md flex flex-col bg-white">
            <div>
              {/* Table Header */}
              <div className="grid grid-cols-3 border-b-2 border-[#c0392b] bg-slate-50">
                <div className="px-4 py-3 border-r border-[#c0392b]/40">
                  <span className="font-extrabold text-[#c0392b] text-xs sm:text-sm uppercase tracking-wide">
                    BMI (KG/M²)
                  </span>
                </div>
                <div className="px-4 py-3 text-center border-r border-[#c0392b]/40">
                  <span className="font-extrabold text-[#c0392b] text-xs sm:text-sm uppercase tracking-wide">WORLD</span>
                </div>
                <div className="px-4 py-3 text-center">
                  <span className="font-extrabold text-[#c0392b] text-xs sm:text-sm uppercase tracking-wide">ASIAN</span>
                </div>
              </div>

              {/* Table Rows */}
              {bmiCategories.map((cat, idx) => {
                const isActive = activeCategory?.label === cat.label;
                return (
                  <div
                    key={idx}
                    className={`grid grid-cols-3 border-b border-[#c0392b]/25 transition-all duration-300 ${isActive ? 'ring-2 ring-inset ring-[#800000]' : ''}`}
                  >
                    {/* Category Label */}
                    <div
                      className="px-4 py-4 flex items-center border-r border-[#c0392b]/25"
                      style={{ background: isActive ? '#fff5f5' : '#fff' }}
                    >
                      <span
                        className="font-bold text-slate-700 text-xs sm:text-sm uppercase tracking-wide leading-tight"
                        style={{ color: isActive ? '#800000' : undefined }}
                      >
                        {cat.label}
                      </span>
                    </div>

                    {/* WORLD Value Cell */}
                    <div
                      className="flex items-center justify-center px-3 py-4 border-r border-[#c0392b]/25"
                      style={{ background: cat.worldBg || '#fff' }}
                    >
                      <span
                        className="font-bold text-base sm:text-lg"
                        style={{ color: cat.worldText }}
                      >
                        {cat.world}
                      </span>
                    </div>

                    {/* ASIAN Value Cell */}
                    <div
                      className="flex items-center justify-center px-3 py-4"
                      style={{ background: cat.asianBg || '#fff' }}
                    >
                      <span
                        className="font-bold text-base sm:text-lg"
                        style={{ color: cat.asianText }}
                      >
                        {cat.asian}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Note */}
            <div className="p-3 bg-slate-50 border-t border-slate-200">
              <p className="text-[11px] text-slate-400 text-center leading-relaxed italic">
                Note: BMI is a general indicator and does not account for muscle mass, bone density, or overall body composition.
              </p>
            </div>
          </div>

        </div>

        {/* ── 3. Header Section – Health Impacts of Obesity ─────────────── */}
        {/* <div className="text-center max-w-3xl mx-auto mb-16"> */}
        {/* <div className="text-center max-w-3xl mx-auto mb-16"> */}
        {/* <div className="text-center max-w-4xl mx-auto mb-20 md:mb-24"> */}
        <div className="text-center max-w-4xl mx-auto pt-4 md:pt-5 mb-10 md:mb-12">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-widest text-[#800000] uppercase rounded-full bg-red-50">
            {t('challenge_subtitle')}
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('challenge_title')}
          </h2>
          {/* <p className="text-lg text-slate-600 leading-relaxed mb-8"> */}
          <p className="text-lg text-slate-600 leading-relaxed mb-10 italic font-bold">
            {t('challenge_description')}
          </p>

          {Array.isArray(t('challenge_points')) && (
            // <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 text-left mt-4">
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-5 text-left mt-6 mb-6">
              {
                t('challenge_points').map((point, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                    <span className="font-bold text-slate-700 text-sm md:text-base leading-tight">{point}</span>
                  </div>
                ))
              }
            </div>
          )}
        </div>

        {/* ── 4. Clinical Evaluation ─────────────── */}
        <div className="max-w-5xl mx-auto mb-10">
          <div className="bg-white p-10 rounded-3xl border border-slate-100 shadow-sm flex flex-col">

            {/* Header Section */}
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-50 rounded-2xl">
                <Stethoscope className="text-blue-600 w-7 h-7" />
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Clinical Evaluation
              </h3>
            </div>

            {/* Divider */}
            <hr className="border-slate-100 mb-8" />

            {/* Content Section */}
            <div className="grid md:grid-cols-2 gap-6">

              {/* Why Evaluate Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 transition-all hover:shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <h4 className="font-bold text-slate-800 text-lg">
                    Why Evaluate?
                  </h4>
                </div>
                <p className="text-slate-600 leading-relaxed text-sm">
                  Obesity is a complex metabolic condition. A professional assessment identifies
                  risks like Type 2 diabetes and hypertension before they progress.
                </p>
              </div>

              {/* The Process Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 transition-all hover:shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  <h4 className="font-bold text-slate-800 text-lg">
                    The Process
                  </h4>
                </div>
                <ul className="space-y-2">
                  {[
                    "- Physical examination.",
                    "- Lipid panel blood tests.",
                    "- Review of Metabolic markers with your primary care Physician."
                  ].map((item, i) => (
                    <li key={i} className="text-slate-600 leading-relaxed text-sm">{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
            @keyframes autoscale {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.03); }
            }
            .animate-autoscale {
              animation: autoscale 4s ease-in-out infinite;
            }
            /* Hide number input spinners */
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type=number] {
              -moz-appearance: textfield;
            }
          `}
        </style>
      </div>
    </section >
  );
};

export default ChallengeSection;